import store from '@/store/store';
import WhatsAppService from '@/services/WhatsAppService';

const normalizeBaseUrl = (url) => (url ? url.replace(/\/$/, '') : '');

const parseErrorDetail = (detail) => {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object' && detail.message) return detail.message;
  return null;
};

let cachedAccessToken = null;

class WhatsAppTemplatesService {
  static clearAccessTokenCache() {
    cachedAccessToken = null;
  }

  static getBaseUrl() {
    const { tokkoMessengerURL } = store.getState().UserPreferencesSlice;
    const fromStorage = localStorage.getItem('tokkoChatHost');

    return normalizeBaseUrl(tokkoMessengerURL || fromStorage);
  }

  static async getAccessToken({ forceRefresh = false } = {}) {
    if (forceRefresh) {
      WhatsAppTemplatesService.clearAccessTokenCache();
    }

    const messengerData = store.getState().MessengerSlice.data;
    const messengerToken =
      messengerData?.accessToken || messengerData?.userData?.accessToken;

    if (messengerToken) {
      cachedAccessToken = messengerToken;
      return messengerToken;
    }

    if (cachedAccessToken) {
      return cachedAccessToken;
    }

    const authData = await WhatsAppService.getWebSocketAccessToken();
    cachedAccessToken = authData?.accessToken || null;
    return cachedAccessToken;
  }

  static async request(path, options = {}, hasRetriedAuth = false) {
    const baseUrl = WhatsAppTemplatesService.getBaseUrl();

    if (!baseUrl) {
      throw new Error('Messenger URL is not configured');
    }

    const accessToken = await WhatsAppTemplatesService.getAccessToken();

    if (!accessToken) {
      throw new Error('Messenger access token is not available');
    }

    const response = await fetch(`${baseUrl}${path}`, {
      credentials: 'include',
      ...options,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...options.headers,
      },
    });

    if (
      !response.ok &&
      (response.status === 401 || response.status === 403) &&
      !hasRetriedAuth
    ) {
      WhatsAppTemplatesService.clearAccessTokenCache();
      return WhatsAppTemplatesService.request(path, options, true);
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      throw new Error(
        parseErrorDetail(errorBody.detail) || `HTTP ${response.status}`
      );
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  static getCompanyId() {
    return store.getState().UserSlice.data?.company_id;
  }

  static getTemplates() {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request(
      `/templates?company_id=${companyId}`
    );
  }

  static getVariables() {
    return WhatsAppTemplatesService.request('/templates/variables');
  }

  static createTemplate(payload) {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request('/templates', {
      method: 'POST',
      body: JSON.stringify({ ...payload, company_id: companyId }),
    });
  }

  static getTemplate(templateId) {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request(
      `/templates/${templateId}?company_id=${companyId}`
    );
  }

  static updateTemplate(templateId, payload) {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify({ ...payload, company_id: companyId }),
    });
  }

  static assignParameters(templateId, parameters) {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request(
      `/templates/${templateId}/parameters`,
      {
        method: 'PUT',
        body: JSON.stringify({ parameters, company_id: companyId }),
      }
    );
  }

  static deleteTemplate(templateId) {
    const companyId = WhatsAppTemplatesService.getCompanyId();
    return WhatsAppTemplatesService.request(
      `/templates/${templateId}?company_id=${companyId}`,
      { method: 'DELETE' }
    );
  }
}

export default WhatsAppTemplatesService;
